(() => {
  const BTN_SELECTOR = '.pw-load-more';
  const WRAP_SELECTOR = '.pw-load-more-wrap';
  const LIST_SELECTOR = 'product-list.product-list, .product-list';
  const STATUS_SELECTOR = '.pw-loadmore-status';

  const KEY_POS = 'pw_collection_scroll_y';
  const KEY_URL = 'pw_collection_url';

  let isLoading = false;
  let io = null;

  function isCollectionPage() {
    return document.body && document.body.className.includes('template-collection');
  }

  function getBtn() {
    return document.querySelector(BTN_SELECTOR);
  }
  function getWrap() {
    return document.querySelector(WRAP_SELECTOR);
  }
  function getStatus() {
    return document.querySelector(STATUS_SELECTOR);
  }

  function pwSetLoading(on) {
    const s = getStatus();
    if (!s) return;
    s.hidden = !on;
    s.setAttribute('aria-busy', on ? 'true' : 'false');
  }

  function setBtnLoading(btn, on) {
    if (!btn) return;
    btn.classList.toggle('is-loading', !!on);
    btn.setAttribute('aria-busy', on ? 'true' : 'false');
    btn.disabled = !!on;
  }

  function removeLoadMoreUI() {
    const wrap = getWrap();
    if (wrap) wrap.remove();
    pwSetLoading(false);
    if (io) io.disconnect();
    io = null;
  }

  // Save scroll position (throttled)
  function initScrollSaver() {
    let t = null;
    window.addEventListener(
      'scroll',
      () => {
        if (t) return;
        t = setTimeout(() => {
          try {
            sessionStorage.setItem(KEY_POS, String(window.scrollY || 0));
            sessionStorage.setItem(KEY_URL, location.href.split('#')[0]);
          } catch (e) {}
          t = null;
        }, 150);
      },
      { passive: true }
    );
  }

  // Restore scroll position when navigating back
  function restoreScrollIfNeeded() {
    try {
      const savedUrl = sessionStorage.getItem(KEY_URL);
      const savedY = parseInt(sessionStorage.getItem(KEY_POS) || '0', 10);

      if (savedUrl && savedUrl === location.href.split('#')[0] && savedY > 0) {
        requestAnimationFrame(() => window.scrollTo(0, savedY));
      }
    } catch (e) {}
  }

  /**
   * Some theme lazy systems only respond to "real" user input events (wheel/touch)
   * and/or read scrollTop from the actual scrollingElement.
   * This function nudges those systems WITHOUT changing the final scroll position.
   *
   * It does:
   *  - wheel + touchmove events
   *  - scroll on document.scrollingElement (1px down then back)
   *  - scroll event dispatches (belt & braces)
   *
   * No preloading. No eager loading. No extra bytes.
   */
  function wakeThemeLazyPipeline() {
    const se = document.scrollingElement || document.documentElement || document.body;
    if (!se) return;

    const y = se.scrollTop || window.scrollY || 0;

    const fire = (target, ev) => {
      try {
        target.dispatchEvent(ev);
      } catch (e) {}
    };

    // Fire "input grade" events first
    try {
      const wheelEv = new WheelEvent('wheel', { deltaY: 1, bubbles: true, cancelable: true });
      fire(window, wheelEv);
      fire(document, wheelEv);
      fire(se, wheelEv);
    } catch (e) {}

    try {
      // touchmove can't be constructed in all browsers; best-effort
      const touchEv = new Event('touchmove', { bubbles: true, cancelable: true });
      fire(window, touchEv);
      fire(document, touchEv);
      fire(se, touchEv);
    } catch (e) {}

    // Now do the minimal scroll nudge on the actual scrolling element
    // Safari sometimes needs a tiny delay after DOM insertion.
    setTimeout(() => {
      try {
        se.scrollTop = y + 1;
      } catch (e) {
        try {
          window.scrollTo(0, y + 1);
        } catch (e2) {}
      }

      // Dispatch scroll events (some systems listen rather than observe)
      try {
        fire(window, new Event('scroll'));
        fire(document, new Event('scroll'));
        fire(se, new Event('scroll'));
      } catch (e) {}

      // Back to the original position
      requestAnimationFrame(() => {
        try {
          se.scrollTop = y;
        } catch (e) {
          try {
            window.scrollTo(0, y);
          } catch (e2) {}
        }

        try {
          fire(window, new Event('scroll'));
          fire(document, new Event('scroll'));
          fire(se, new Event('scroll'));
        } catch (e) {}

        // Also resize (some sliders recalc on resize after lazy media attaches)
        try {
          fire(window, new Event('resize'));
        } catch (e) {}
      });
    }, 60);
  }

  async function loadNext(btn, { updateUrl = false } = {}) {
    if (isLoading) return;

    const nextUrl = btn?.getAttribute('data-next-url');
    if (!nextUrl) {
      removeLoadMoreUI();
      return;
    }

    isLoading = true;
    setBtnLoading(btn, true);
    pwSetLoading(true);

    try {
      const res = await fetch(nextUrl, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const currentList = document.querySelector(LIST_SELECTOR);
      const nextList = doc.querySelector(LIST_SELECTOR);
      if (!currentList || !nextList) throw new Error('Product list not found');

      // Append new items
      Array.from(nextList.children).forEach((el) => currentList.appendChild(el));

      // ✅ The only thing we're doing post-append:
      // wake the theme's lazy/hover pipeline the same way a real scroll does.
      wakeThemeLazyPipeline();

      // Update next url (or remove UI)
      const nextBtn = doc.querySelector(BTN_SELECTOR);
      const newNextUrl = nextBtn?.getAttribute('data-next-url');

      if (newNextUrl) {
        btn.setAttribute('data-next-url', newNextUrl);
      } else {
        btn.removeAttribute('data-next-url');
        removeLoadMoreUI();
      }

      if (updateUrl) {
        history.replaceState({ pwInfScroll: true }, '', nextUrl);
      }
    } catch (e) {
      console.error('[pw-load-more] failed:', e);
    } finally {
      setBtnLoading(btn, false);
      pwSetLoading(false);
      isLoading = false;
    }
  }

  function initAutoLoad() {
    const btn = getBtn();
    if (!btn) return;

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      loadNext(btn);
    });

    io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) loadNext(btn, { updateUrl: false });
        }
      },
      { rootMargin: '800px 0px 800px 0px' }
    );

    io.observe(btn);
  }

  document.addEventListener('DOMContentLoaded', () => {
    if (!isCollectionPage()) return;
    initScrollSaver();
    restoreScrollIfNeeded();
    initAutoLoad();
  });

  window.addEventListener('pageshow', (e) => {
    if (!isCollectionPage()) return;
    if (e.persisted) restoreScrollIfNeeded();
  });
})();
